import { IBanner, IBannerDependsOnPromocode, TBannerLocation } from '../banners.t';
import Banner from '../Banner/Banner';
import React, { useCallback, useMemo } from 'react';
import { bannersSlice } from '../bannersReducer';
import { useDispatch, useSelector } from 'react-redux';
import { LOCALE_CODES, LOG_AF_EVENT_NAMES, LOG_EVENT_TYPES } from '../../../app/appConstants';
import { BANNER_ACTION_TYPES, BANNER_DEPENDS_ON_PARAMS } from '../bannersConstants';
import appHistory from '../../../app/appHistory';
import { getUserLocale, getUserSelectedAccountType } from '../../../app/appSelectors';
import useBannerDependsOnPromocodeConfig from '../hooks/useBannersDependsOnPromocode';
import _ from 'lodash';
import { appLog, logAppsflyerAction } from '../../../app/appActions';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../../../app/api';
import fillAppsflyerEventNameParams from '../../../utils/fillAppsflyerEventNameParams';
import { getIsLocationPlatformDesktopSelector } from '../../Location/locationSelectors';
import getDesktopPlatformSearchByMobileSearch from '../../Location/utils/getDesktopPlatformSearchByMobileSearch';

interface IBannerContainerProps {
    data: IBanner,
    location: TBannerLocation,
    short?: boolean,
}

function BannerContainer(props: IBannerContainerProps) {
    const { data, location, short } = props;
    const locale = useSelector(getUserLocale);
    const dispatch = useDispatch();
    const accountType = useSelector(getUserSelectedAccountType);
    const { pathname } = useLocation();
    const dependsOnPromocodeConfig = useMemo(() => data.depends_on?.find((item) =>
        item.param === BANNER_DEPENDS_ON_PARAMS.PROMOCODE) as IBannerDependsOnPromocode, [data]);
    const isPlatformDesktop = useSelector(getIsLocationPlatformDesktopSelector);
    const { promocodeSecondsLeft } = useBannerDependsOnPromocodeConfig(dependsOnPromocodeConfig);

    const handleLogAppsflyer = useCallback((source: 'banner' | 'button' | 'close') => {
        dispatch(logAppsflyerAction({
            name: fillAppsflyerEventNameParams(LOG_AF_EVENT_NAMES.BANNER_CLICK, { location, name: data.name }),
            payload: {
                account_type: accountType,
                source,
            },
        }));
    }, [accountType, data, dispatch, location]);

    const handleAppLog = useCallback((source: 'banner' | 'button' | 'close') => {
        dispatch(appLog({
            type: LOG_EVENT_TYPES.BANNER,
            payload: {
                action: 'click',
                location,
                name: data.name,
                source,
            },
        }));
    }, [data, dispatch, location]);

    const handleBannerEvent = useCallback((source: 'banner' | 'button' | 'close') => {
        handleLogAppsflyer(source);
        handleAppLog(source);
    }, [handleAppLog, handleLogAppsflyer]);

    const onClickHandler = useCallback(() => {
        handleBannerEvent(short ? 'banner' : 'button');
        if (!data.action_type || !data.action_data) return;
        const actionDataByLocale = _.mapValues(_.keyBy(data.action_data, 'locale'), 'url');

        let url = actionDataByLocale[locale] || actionDataByLocale[LOCALE_CODES.en];
        if (isPlatformDesktop) {
            url = getDesktopPlatformSearchByMobileSearch(url);
        }
        switch (data.action_type) {
            case BANNER_ACTION_TYPES.EXTERNAL_URL:
                window.open(url, '_blank');
                break;
            case BANNER_ACTION_TYPES.INTERNAL_URL:
                try {
                    const currentUrl = new URL(url, window.location.origin);
                    const redirectPathname = !currentUrl.pathname || currentUrl.pathname === '/' ? pathname : currentUrl.pathname;
                    appHistory.push(`${redirectPathname}?${currentUrl.searchParams.toString()}`);
                } catch (e) {
                    console.error('Invalid url', url);
                }
                break;
            default:
                break;
        }
    }, [data, handleBannerEvent, locale, pathname, short, isPlatformDesktop]);

    const onCloseHandler = useCallback((bannerName, location) => {
        handleBannerEvent('close');
        dispatch(bannersSlice.actions.closeBannerAction({ name: bannerName, location }));
    }, [dispatch, handleBannerEvent]);

    const imageUrl = useMemo(() => {
        return data.img_file_name ? `${API_URL}/files/${data.img_file_name}` : (data.img_data || '');
    }, [data]);

    return <Banner
        background={data.ui_config.background}
        backgroundImageSize={data.ui_config.img_height ? `auto ${data.ui_config.img_height}px` : undefined}
        backgroundImageUrl={imageUrl}
        bannerName={data.name}
        buttonBackground={data.ui_config.button_background}
        buttonTitle={data.button_title}
        description={data.description}
        key={data.name}
        location={location}
        onClick={onClickHandler}
        onClose={onCloseHandler}
        short={short}
        textColor={data.ui_config.text_color}
        timerSecondsLeft={promocodeSecondsLeft}
        title={data.title}
    />;
}

export default BannerContainer;
